/**
 * Latest-turn-wins state transitions shared by text, voice, and legacy senders.
 * The transport and store are structural so this policy stays easy to test.
 */

export interface TurnLifecycleState {
  clearCurrentTurn: () => void
  setCurrentTurnId: (turnId: string | null) => void
  setLoading: (loading: boolean) => void
  setInterrupted: (interrupted: boolean) => void
  setAudioPlaying: (playing: boolean) => void
  setGreetingLoading: (loading: boolean) => void
}

interface StartLatestTurnOptions {
  state: TurnLifecycleState
  getCurrentTurnId: () => string | null
  turnId: string
  sendInterrupt: (turnId: string) => boolean
  sendMessage: (turnId: string) => boolean
}

interface InterruptActiveTurnOptions {
  state: TurnLifecycleState
  getCurrentTurnId: () => string | null
  sendInterrupt: (turnId: string) => boolean
}

function settleLocalTurn(
  state: TurnLifecycleState,
  interrupted: boolean,
): void {
  state.clearCurrentTurn()
  state.setLoading(false)
  state.setAudioPlaying(false)
  state.setGreetingLoading(false)
  state.setInterrupted(interrupted)
}

export function startLatestTurn({
  state,
  getCurrentTurnId,
  turnId,
  sendInterrupt,
  sendMessage,
}: StartLatestTurnOptions): boolean {
  const previousTurnId = getCurrentTurnId()
  if (previousTurnId) {
    // A new question supersedes the old one immediately. The server also
    // enforces this policy, so a failed interrupt frame must not retain A.
    sendInterrupt(previousTurnId)
  }

  settleLocalTurn(state, false)
  state.setCurrentTurnId(turnId)
  state.setInterrupted(false)
  state.setLoading(true)

  const sent = sendMessage(turnId)
  if (!sent && getCurrentTurnId() === turnId) {
    settleLocalTurn(state, false)
  }
  return sent
}

export function interruptActiveTurn({
  state,
  getCurrentTurnId,
  sendInterrupt,
}: InterruptActiveTurnOptions): boolean {
  const turnId = getCurrentTurnId()
  if (!turnId) return false

  const sent = sendInterrupt(turnId)
  if (sent && getCurrentTurnId() === turnId) {
    settleLocalTurn(state, true)
  }
  return sent
}
